import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { chainsApi, transactionsApi, ordersApi } from '@/lib/api/client';
import type { Transaction, OpenOrder } from '@/lib/api/client';
import type { TradeChainWithPositions } from '@steward/shared';
import { cn } from '@/lib/utils';

export function SymbolSearch() {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [debouncedSymbol, setDebouncedSymbol] = useState('');
  const [chains, setChains] = useState<TradeChainWithPositions[]>([]);
  const [txns, setTxns] = useState<Transaction[]>([]);
  const [orders, setOrders] = useState<OpenOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const symbol = query.trim().toUpperCase();

  // Debounce: wait 300ms after typing stops before fetching
  useEffect(() => {
    if (!symbol || symbol.length < 1) { setDebouncedSymbol(''); return; }
    const timer = setTimeout(() => setDebouncedSymbol(symbol), 300);
    return () => clearTimeout(timer);
  }, [symbol]);

  // Fetch data when debounced symbol changes
  useEffect(() => {
    if (!debouncedSymbol) {
      setChains([]); setTxns([]); setOrders([]);
      return;
    }
    setLoading(true);
    Promise.all([
      chainsApi.list({ symbol: debouncedSymbol, limit: 50 }).catch(() => ({ chains: [] })),
      transactionsApi.list({ symbol: debouncedSymbol, limit: 3, sort: 'desc' }).catch(() => ({ transactions: [] })),
      ordersApi.getOpen().catch(() => ({ orders: [] as OpenOrder[] })),
    ]).then(([chainData, txnData, orderData]) => {
      setChains(chainData.chains || []);
      setTxns(txnData.transactions || []);
      // Filter orders client-side by symbol
      const symbolOrders = (orderData.orders || []).filter(o =>
        o.legs.some(leg => {
          const sym = leg.underlyingSymbol || leg.symbol;
          return sym?.toUpperCase() === debouncedSymbol;
        })
      );
      setOrders(symbolOrders);
      setLoading(false);
    });
  }, [debouncedSymbol]);

  // Close on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  // Keyboard shortcut: / to focus, Escape to close
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === '/' && !e.metaKey && !e.ctrlKey) {
        const tag = (e.target as HTMLElement)?.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
        e.preventDefault();
        inputRef.current?.focus();
      }
      if (e.key === 'Escape') {
        setOpen(false);
        inputRef.current?.blur();
      }
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, []);

  const go = useCallback((path: string) => {
    navigate(path);
    setQuery('');
    setOpen(false);
    inputRef.current?.blur();
  }, [navigate]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && symbol) {
      go(`/symbols/${symbol}`);
    }
  };

  const openChains = chains.filter(c => c.status === 'open' || c.status === 'partial');
  const closedChains = chains.filter(c => c.status === 'closed');
  const hasData = chains.length > 0 || txns.length > 0 || orders.length > 0;

  function formatDate(d: Date | string) {
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  return (
    <div ref={containerRef} className="relative min-w-0">
      <div className="relative">
        <svg
          className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400"
          fill="none" viewBox="0 0 24 24" stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <input
          ref={inputRef}
          type="text"
          placeholder="Symbol… ( / )"
          value={query}
          onChange={e => { setQuery(e.target.value.toUpperCase()); setOpen(true); }}
          onFocus={() => { if (symbol) setOpen(true); }}
          onKeyDown={handleKeyDown}
          // w-40 fits "Symbol… ( / )" whole (it needs ~147px with the padding; w-32 cut the
          // closing paren). max-w-full lets it give ground on a phone narrower than the one it
          // was sized on, rather than pushing the avatar off the header.
          className="w-40 max-w-full sm:w-44 pl-8 pr-2 py-1.5 text-sm border border-gray-200 rounded-lg bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent placeholder:text-gray-400"
        />
      </div>

      {open && symbol.length >= 1 && (
        <div className="absolute right-0 mt-1.5 w-80 rounded-lg bg-white shadow-lg ring-1 ring-black/10 z-50 overflow-hidden max-h-[28rem] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100">
            <div>
              <span className="text-xs text-gray-500">Results for </span>
              <span className="text-sm font-bold text-gray-900">{symbol}</span>
            </div>
            {loading && <span className="text-xs text-gray-400 animate-pulse">loading...</span>}
          </div>

          {/* No data state */}
          {!loading && debouncedSymbol && !hasData && (
            <div className="px-3 py-4 text-center text-sm text-gray-500">
              No data found for {symbol}
            </div>
          )}

          {/* Open Chains */}
          {openChains.length > 0 && (
            <div className="border-b border-gray-100">
              <button
                onClick={() => go(`/chains?symbol=${symbol}&status=open`)}
                className="flex items-center justify-between w-full px-3 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wide hover:bg-gray-50"
              >
                <span>Open Chains ({openChains.length})</span>
                <span className="text-gray-400">View all &rarr;</span>
              </button>
              {openChains.slice(0, 3).map(chain => (
                <Link
                  key={chain.id}
                  to={`/chains/${chain.id}`}
                  onClick={() => { setQuery(''); setOpen(false); }}
                  className="flex items-center justify-between px-3 py-2 hover:bg-gray-50 text-sm"
                >
                  <div className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-green-500" />
                    <span className="font-medium text-gray-900">{chain.strategyType || 'Position'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <span>{chain.positions?.[0]?.legs?.[0] ? `${chain.positions[0].legs[0].strikePrice}${chain.positions[0].legs[0].optionType === 'put' ? 'P' : 'C'}` : ''}</span>
                    <span className="text-green-600 font-medium">
                      ${((chain.totalPremiumCollected || 0) - (chain.totalPremiumPaid || 0)).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {/* Open Orders */}
          {orders.length > 0 && (
            <div className="border-b border-gray-100">
              <button
                onClick={() => go(`/orders`)}
                className="flex items-center justify-between w-full px-3 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wide hover:bg-gray-50"
              >
                <span>Open Orders ({orders.length})</span>
                <span className="text-gray-400">View &rarr;</span>
              </button>
              {orders.slice(0, 2).map(order => (
                <div key={order.orderId} className="flex items-center justify-between px-3 py-2 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-yellow-500" />
                    <span className="text-gray-700">
                      {order.legs.map(leg => {
                        const strike = leg.strikePrice;
                        const type = leg.putCall;
                        return strike && type ? `${strike}${type[0]}` : leg.symbol;
                      }).join(' / ')}
                    </span>
                  </div>
                  <span className="text-xs text-gray-400">{order.status}</span>
                </div>
              ))}
            </div>
          )}

          {/* Recent Transactions */}
          {txns.length > 0 && (
            <div className="border-b border-gray-100">
              <button
                onClick={() => go(`/transactions?symbol=${symbol}`)}
                className="flex items-center justify-between w-full px-3 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wide hover:bg-gray-50"
              >
                <span>Recent Transactions</span>
                <span className="text-gray-400">View all &rarr;</span>
              </button>
              {txns.map(txn => (
                <div key={txn.id} className="flex items-center justify-between px-3 py-1.5 text-xs text-gray-600">
                  <div className="flex items-center gap-2">
                    <span className="text-gray-400 tabular-nums">{formatDate(txn.executedAt)}</span>
                    <span>{txn.action}</span>
                    {txn.strikePrice && <span className="text-gray-400">{txn.strikePrice}{txn.optionType === 'call' ? 'C' : 'P'}</span>}
                  </div>
                  <span className={cn('tabular-nums font-medium', txn.amount >= 0 ? 'text-green-600' : 'text-red-600')}>
                    {txn.amount >= 0 ? '' : '-'}${Math.abs(txn.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Closed chains summary */}
          {closedChains.length > 0 && (
            <button
              onClick={() => go(`/chains?symbol=${symbol}`)}
              className="flex items-center justify-between w-full px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
            >
              <span>{closedChains.length} closed chain{closedChains.length !== 1 ? 's' : ''}</span>
              <span className={cn('font-medium tabular-nums',
                closedChains.reduce((s, c) => s + c.realizedPnl, 0) >= 0 ? 'text-green-600' : 'text-red-600'
              )}>
                ${closedChains.reduce((s, c) => s + c.realizedPnl, 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </span>
            </button>
          )}

          {/* Quick link: Symbol Overview */}
          <button
            onClick={() => go(`/symbols/${symbol}`)}
            className="flex items-center gap-2 w-full px-3 py-2 text-xs text-brand-600 hover:bg-brand-50 font-medium border-t border-gray-100"
          >
            View full {symbol} overview &rarr;
          </button>
        </div>
      )}
    </div>
  );
}
